import React from "react";
import ReactDOM from "react-dom/client";
import r2wc from "@r2wc/react-to-web-component";
import { QueryClientProvider } from "@tanstack/react-query";
import { getQueryClient } from "./widgets/lib/query-client";
import { Reviews } from "./widgets/components/Reviews";
import type { ReviewsProps } from "./widgets/components/Reviews";
import { ReviewsDialogOverlay } from "./widgets/components/Reviews/ReviewsDialogOverlay";
import type { ReviewsDialogOverlayProps } from "./widgets/components/Reviews/ReviewsDialogOverlay";
import { loadAndApplyThemeOverrides } from "./widgets/lib/theme";

// Import styles - will be compiled by Tailwind and available via ?inline
import cssText from "./widgets/styles/globals.css?inline";

// Flag to track if fonts have been injected into document head
let fontsInjected = false;

// Extract @font-face rules from CSS and inject them into document head
function injectFontsIntoDocument() {
  if (fontsInjected || typeof document === "undefined") return;
  fontsInjected = true;

  const css =
    cssText ||
    (window as unknown as { __BALUU_REVIEWS_CSS__?: string })
      .__BALUU_REVIEWS_CSS__ ||
    "";

  if (!css) return;

  // Extract all @font-face rules using regex
  const fontFaceRegex = /@font-face\s*\{[^}]+\}/g;
  const fontFaceRules = css.match(fontFaceRegex);

  if (fontFaceRules && fontFaceRules.length > 0) {
    const fontStyle = document.createElement("style");
    fontStyle.id = "baluu-reviews-fonts";
    fontStyle.textContent = fontFaceRules.join("\n");
    document.head.appendChild(fontStyle);
  }
}

// Wrapper component that provides React Query context
function ReviewsWithProviders(props: ReviewsProps) {
  const queryClient = getQueryClient();

  return React.createElement(
    QueryClientProvider,
    { client: queryClient },
    React.createElement(Reviews, props),
  );
}

// Create the web component with Shadow DOM for style isolation
const ReviewsWebComponent = r2wc(ReviewsWithProviders, {
  props: {
    businessSlug: "string",
    apiUrl: "string",
    listingId: "string",
    listingType: "string",
    scope: "string",
    theme: "string",
  },
  shadow: "open",
});

// Create the reviews dialog overlay web component
const ReviewsDialogWebComponent = r2wc(ReviewsDialogOverlay, {
  props: {
    businessSlug: "string",
    apiUrl: "string",
    listingId: "string",
    listingType: "string",
    scope: "string",
  },
  shadow: "open",
});

// Type for custom element with lifecycle methods
type CustomElementWithLifecycle = HTMLElement & {
  connectedCallback?(): void;
};

// Helper function to inject CSS into shadow DOM
function injectCssIntoShadow(element: HTMLElement) {
  const shadowRoot = (element as HTMLElement & { shadowRoot: ShadowRoot })
    .shadowRoot;
  if (shadowRoot) {
    let css =
      cssText ||
      (window as unknown as { __BALUU_REVIEWS_CSS__?: string })
        .__BALUU_REVIEWS_CSS__ ||
      "";

    if (css) {
      // Transform :root to :host for Shadow DOM compatibility
      css = css.replace(/:root\s*\{/g, ":host {");

      const style = document.createElement("style");
      style.textContent = css;
      shadowRoot.prepend(style);
    }
  }
}

// Custom element that injects CSS into shadow DOM and loads theme overrides
class BaluuReviews extends (ReviewsWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    // Inject fonts into document head (only once)
    injectFontsIntoDocument();

    // Call parent's connectedCallback if it exists
    const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
    if (parent?.connectedCallback) {
      parent.connectedCallback.call(this);
    }

    // Inject CSS into shadow DOM
    injectCssIntoShadow(this);

    // Fetch and apply theme overrides from API (async, with caching)
    // Supports both business-slug and business-id for backwards compatibility
    const businessIdentifier = this.getAttribute("business-slug") || this.getAttribute("business-id");
    const apiUrl = this.getAttribute("api-url") || undefined;
    
    if (businessIdentifier && this.shadowRoot) {
      loadAndApplyThemeOverrides(businessIdentifier, this.shadowRoot, apiUrl);
    }
  }
}

// Reviews dialog overlay web component with CSS injection and full-screen overlay styling
class BaluuReviewsDialog extends (ReviewsDialogWebComponent as unknown as {
  new (): CustomElementWithLifecycle;
}) {
  connectedCallback() {
    // Inject fonts into document head (only once)
    injectFontsIntoDocument();

    // Call parent's connectedCallback if it exists
    const parent = Object.getPrototypeOf(Object.getPrototypeOf(this));
    if (parent?.connectedCallback) {
      parent.connectedCallback.call(this);
    }

    // Style the host element as a full-screen fixed overlay
    this.style.cssText = `
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
    `;

    // Inject CSS into shadow DOM for style isolation
    injectCssIntoShadow(this);

    // Fetch and apply theme overrides from API (async, with caching)
    // Supports both business-slug and business-id for backwards compatibility
    const businessIdentifier = this.getAttribute("business-slug") || this.getAttribute("business-id");
    const apiUrl = this.getAttribute("api-url") || undefined;
    
    if (businessIdentifier && this.shadowRoot) {
      loadAndApplyThemeOverrides(businessIdentifier, this.shadowRoot, apiUrl);
    }
  }
}

// Register the custom elements
if (typeof window !== "undefined") {
  if (!customElements.get("baluu-reviews")) {
    customElements.define("baluu-reviews", BaluuReviews);
  }
  if (!customElements.get("baluu-reviews-dialog")) {
    customElements.define("baluu-reviews-dialog", BaluuReviewsDialog);
  }
}

// Export for programmatic usage
export { Reviews, ReviewsWithProviders, ReviewsDialogOverlay };
export type { ReviewsProps, ReviewsDialogOverlayProps };

// Also expose on window for UMD builds
if (typeof window !== "undefined") {
  (window as unknown as { BaluuReviews: unknown }).BaluuReviews = {
    Reviews,
    ReviewsWithProviders,
    // Helper to manually render a React component
    render: (container: HTMLElement, props: ReviewsProps) => {
      const queryClient = getQueryClient();
      const root = ReactDOM.createRoot(container);
      root.render(
        React.createElement(
          QueryClientProvider,
          { client: queryClient },
          React.createElement(Reviews, props),
        ),
      );
      return root;
    },
  };
}
